import React, { useMemo } from 'react';

// helpers
import { colorsTheme } from 'resources/theme/styled/colors';
import { AdditionalFieldTypes } from 'enums/onboarding/crm';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { formatFromJSON, formatToJSON } from 'helpers/crmHelpers';
import { supplementaryQuestionsValidationSchema } from 'validations/intake-form/intake-form-steps';
import { AdditionalFieldFormItemWithNullableIdModel } from 'components/Forms/FormComponents/AdditionalField';
import {
  AdditionalFieldAnswerFromQuery,
  SupplementaryQuestionsFormFromQuery,
} from 'api/crm/intakeFormAPI';
import {
  useEntityIntakeFormContext,
  SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE,
} from '../../EntityIntakeFormContext';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { Form, SectionIntro } from '@ui';

interface IProps {
  formId: string;
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

export interface FormValuesModel extends SubmitButtonsFormValues {
  additionalFields: AdditionalFieldFormItemWithNullableIdModel[];
}

const SupplementaryQuestions = ({
  formId,
  disabled,
  renderNavigateButtonInTitle,
}: IProps) => {
  const {
    intakeForm,
    getSupplementaryQuestionsById,
    updateIntakeFormFields,
    navigateToNextStep,
    navigateToPreviousStep,
  } = useEntityIntakeFormContext();

  const supplementaryQuestionsForm = useMemo(() => {
    return getSupplementaryQuestionsById(formId);
  }, [formId]);

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!supplementaryQuestionsForm || !intakeForm) {
      return null;
    }

    const formatAdditionalFields = (
      supplementaryQuestionsForm: SupplementaryQuestionsFormFromQuery,
      filledAdditionalFields?: AdditionalFieldAnswerFromQuery[],
    ): AdditionalFieldFormItemWithNullableIdModel[] => {
      return supplementaryQuestionsForm.additionalFields.map((e) => {
        const predefinedAF = filledAdditionalFields
          ? filledAdditionalFields.find((ad) => ad.name === e.name)
          : null;

        let formattedValue = predefinedAF
          ? formatFromJSON(e.type, predefinedAF.valueJSON)
          : undefined;

        if (
          [AdditionalFieldTypes.Form, AdditionalFieldTypes.Document].includes(
            e.type,
          ) &&
          formattedValue
        ) {
          if (formattedValue && Array.isArray(formattedValue)) {
            formattedValue = formattedValue.map((e) => {
              const document = intakeForm.metadata.documents[e];
              if (document) {
                return {
                  id: document.id,
                  name: document.files[0].name,
                  fileId: document.files[0].id,
                  file: document.files[0],
                };
              }
            });
          }
        }

        return {
          id: predefinedAF?.id || null,
          name: e.name,
          type: e.type,
          value: formattedValue,
          description: e.description,
          relatedTo: e.relatedTo,
          options: {
            onboardingRequired: e.options.onboardingRequired,
            isForRegulatedOnly: e.options.isForRegulatedOnly,
            documentRequiresCertification:
              e.options.documentRequiresCertification,
            selectOptions: e.options.selectOptions.length
              ? e.options.selectOptions.map((e) => JSON.parse(e.valueJSON))
              : [],
            formDocument: e.options.formDocument,
          },
        };
      });
    };

    return {
      submitType: null,
      additionalFields: formatAdditionalFields(
        supplementaryQuestionsForm,
        intakeForm.fields.supplementaryQuestions?.[formId] || [],
      ),
    };
  }, [supplementaryQuestionsForm, intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }

    await updateIntakeFormFields(
      {
        supplementaryQuestions: {
          [formId]: values.additionalFields.map((af) => ({
            id: af.id,
            name: af.name,
            type: af.type,
            valueJSON: formatToJSON(af.type, af.value),
          })),
        },
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return supplementaryQuestionsForm ? (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        description={supplementaryQuestionsForm.description || undefined}
        title={
          <DivAlignCenter>
            {supplementaryQuestionsForm.name}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton
                navigateToStepKey={`${SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE}${supplementaryQuestionsForm._id}`}
              />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        enableReinitialize
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm formId={formId} />}
        initialValues={initialValues}
        validationSchema={supplementaryQuestionsValidationSchema}
      />
    </>
  ) : null;
};

export default SupplementaryQuestions;
